import { Button, Flex, Typography, theme } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useModalDownload } from '@/hooks/modals';
import type { DateRange, DownloadConfig, ShowFilter } from '@/types';

const { Title } = Typography;

type ModalHeaderProps = {
  title: string;
  closeModal: () => void;
  modalDateRange: DateRange;
  modalSelectedShow: ShowFilter;
  downloadConfig?: DownloadConfig;
};

/**
 * This component is used to render the header of a modal.
 * @param title - The title of the modal.
 * @param closeModal - The function to close the modal.
 * @param modalDateRange - The date range of the modal.
 * @param modalSelectedShow - The selected show of the modal.
 * @param downloadConfig - The download config for the modal.
 * @returns The modal header component.
 */
const ModalHeaderEcho = ({
  title,
  closeModal,
  downloadConfig,
  modalDateRange,
  modalSelectedShow,
}: ModalHeaderProps) => {
  const { token } = theme.useToken();
  const { isDownloading, handleDownload } = useModalDownload();
  return (
    <Flex
      gap={token.marginSM}
      align="center"
      justify="space-between"
      style={{
        position: 'sticky',
        top: 0,
        padding: token.paddingMD,
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        background: token.colorBgContainer,
        zIndex: 999,
      }}
    >
      <Title level={3} style={{ margin: 0, marginInlineStart: token.marginXS }} data-testid="modal-header-title">
        {title}
      </Title>
      {downloadConfig && (
        <Flex style={{ marginInlineStart: 'auto' }} data-testid="header-children">
          <Button
            type="default"
            icon={<downloadConfig.downloadButtonIcon />}
            onClick={() =>
              handleDownload(() => downloadConfig.onDownload(modalDateRange, modalSelectedShow))
            }
            loading={isDownloading}
          >
            {downloadConfig.downloadButtonText || 'Download'}
          </Button>
        </Flex>
      )}
      <Button type="text" aria-label="close" icon={<CloseOutlined />} onClick={closeModal} />
    </Flex>
  );
};

export default ModalHeaderEcho;
